import apiClient, { tokenStore } from './client';

const BASE = '/accounting';

function unpackList<T>(payload: any): T[] {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.results)) return payload.results;
    return [];
}

export interface AccountingDashboard {
    kpis: {
        revenue: string;
        expenses: string;
        net_profit: string;
        accounts_receivable: string;
        accounts_payable: string;
        cash: string;
        bank: string;
        inventory: string;
    };
    counts: {
        journal_entries: number;
        customer_invoices: number;
        customer_payments: number;
        vendor_bills: number;
        pending_vendor_bills: number;
    };
}

export interface AccountType {
    id: number;
    code: string;
    name: string;
    name_ar: string;
    category: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';
    normal_balance: 'debit' | 'credit';
    is_system: boolean;
}

export interface CustomerInvoice {
    id: number;
    number: string;
    customer_name: string;
    customer_phone: string;
    status: string;
    payment_status: string;
    subtotal: string;
    tax_amount: string;
    total: string;
    amount_paid: string;
    amount_remaining: string;
    order_date: string | null;
    expected_delivery_date: string | null;
    created_at: string;
    confirmed_at: string | null;
    delivered_at: string | null;
    invoice_count?: number;
    delivery_order_count?: number;
    sales_order_form_url?: string;
    inventory_delivery_url?: string;
}

export interface CustomerPayment {
    id: number;
    order: number;
    order_number: string;
    customer_name: string;
    order_status: string;
    transaction_id: string;
    amount: string;
    currency: string;
    status: string;
    is_deposit: boolean;
    created_at: string;
    updated_at: string;
}

export interface VendorContact {
    id: number;
    name: string;
    phone: string;
    email: string;
    city: string;
    tax_number: string;
    created_at: string;
}

export interface CustomerCreditNote {
    id: number;
    return_number: string;
    original_order: number;
    original_order_number: string;
    status: string;
    reason: string;
    refund_method: string;
    refund_amount: string;
    restock_items: boolean;
    created_at: string;
    updated_at: string;
}

export interface VendorBill {
    id: number;
    purchase_order: number;
    purchase_order_number: string;
    supplier_name: string;
    bill_number: string;
    amount: string;
    amount_paid: string;
    amount_remaining: string;
    status: 'draft' | 'posted' | 'partial' | 'paid' | 'cancelled';
    posted_at: string | null;
    paid_at: string | null;
    created_at: string;
    bill_count?: number;
    purchase_order_form_url?: string;
    inventory_receipt_url?: string;
    payments: Array<{
        id: number;
        reference: string;
        amount: string;
        payment_method: 'cash' | 'bank' | 'card';
        payment_date: string;
        created_at: string;
    }>;
}

export interface PartnerStatementRow {
    row_type: 'invoice' | 'bill' | 'payment';
    row_label: string;
    date: string;
    reference: string;
    description: string;
    debit: string;
    credit: string;
    running_balance: string;
    status: string;
    source_model: string;
    source_id: number;
}

export interface PartnerStatementPayload {
    partner: {
        id: number;
        name: string;
        phone: string;
        email: string;
        city: string;
        tax_number: string;
        address: string;
        type: 'customer' | 'supplier';
    };
    date_range: {
        start_date?: string;
        end_date?: string;
    };
    summary: {
        total_debit: string;
        total_credit: string;
        balance_due: string;
    };
    rows: PartnerStatementRow[];
}

export interface JournalItem {
    id: number;
    entry: number;
    account: number;
    account_code: string;
    account_name: string;
    analytic_account: number | null;
    analytic_account_name: string;
    description: string;
    debit: string;
    credit: string;
    reference: string;
    created_at: string;
}

export interface JournalEntry {
    id: number;
    number: string;
    journal: number;
    journal_code: string;
    journal_name: string;
    entry_kind: string;
    entry_date: string;
    reference: string;
    description: string;
    total_debit: string;
    total_credit: string;
    source_model: string;
    source_pk: number | null;
    created_by_name: string;
    created_at: string;
    items: JournalItem[];
}

export interface GeneralLedgerRow {
    item_id: number;
    entry_id: number;
    entry_number: string;
    entry_date: string;
    journal_code: string;
    reference: string;
    description: string;
    account_id: number;
    account_code: string;
    account_name: string;
    debit: string;
    credit: string;
    running_balance: string;
}

export interface ProfitLossReport {
    revenue: Array<{ account_id: number; account_code: string; account_name: string; amount: string }>;
    expenses: Array<{ account_id: number; account_code: string; account_name: string; amount: string }>;
    totals: {
        revenue: string;
        expenses: string;
        net_profit: string;
    };
}

export interface BalanceSheetReport {
    as_of_date: string;
    assets: Array<{ account_id: number; account_code: string; account_name: string; balance: string }>;
    liabilities: Array<{ account_id: number; account_code: string; account_name: string; balance: string }>;
    equity: Array<{ account_id: number; account_code: string; account_name: string; balance: string }>;
    totals: {
        assets: string;
        liabilities: string;
        equity: string;
        current_earnings: string;
        liabilities_and_equity: string;
        is_balanced: boolean;
        balance_gap: string;
    };
}

export interface TrialBalanceReport {
    rows: Array<{
        account_id: number;
        account_code: string;
        account_name: string;
        account_type: string;
        debit: string;
        credit: string;
        debit_balance: string;
        credit_balance: string;
    }>;
    totals: {
        debit: string;
        credit: string;
        debit_balance: string;
        credit_balance: string;
    };
}

export interface ChartAccount {
    id: number;
    account_type: number;
    account_type_name: string;
    account_type_category: string;
    code: string;
    name: string;
    name_ar: string;
    description: string;
    allow_reconcile: boolean;
    is_system: boolean;
    is_active: boolean;
    current_balance: string;
}

export interface AccountingJournal {
    id: number;
    code: string;
    name: string;
    name_ar: string;
    journal_type: string;
    sequence: number;
    is_system: boolean;
    is_active: boolean;
}

export interface AnalyticAccount {
    id: number;
    code: string;
    name: string;
    name_ar: string;
    description: string;
    is_active: boolean;
}

export const accountingApi = {
    bootstrap: async () => {
        const { data } = await apiClient.get(`${BASE}/bootstrap/`);
        return data;
    },

    getDashboard: async (): Promise<AccountingDashboard> => {
        const { data } = await apiClient.get(`${BASE}/dashboard/`);
        return data;
    },

    getAccountTypes: async (): Promise<AccountType[]> => {
        if (!tokenStore.getAccess()) return [];
        const { data } = await apiClient.get(`${BASE}/account-types/`);
        return unpackList<AccountType>(data);
    },

    getCustomerInvoices: async (): Promise<CustomerInvoice[]> => {
        if (!tokenStore.getAccess()) return [];
        const { data } = await apiClient.get(`${BASE}/customers/invoices/`);
        return unpackList<CustomerInvoice>(data);
    },

    getCustomerPayments: async (): Promise<CustomerPayment[]> => {
        if (!tokenStore.getAccess()) return [];
        const { data } = await apiClient.get(`${BASE}/customers/payments/`);
        return unpackList<CustomerPayment>(data);
    },

    getCustomers: async (): Promise<VendorContact[]> => {
        if (!tokenStore.getAccess()) return [];
        const { data } = await apiClient.get(`${BASE}/customers/list/`);
        return unpackList<VendorContact>(data);
    },

    createCustomer: async (payload: {
        name: string;
        phone?: string;
        email?: string;
        address?: string;
        city?: string;
        tax_number?: string;
        internal_notes?: string;
    }) => {
        const { data } = await apiClient.post('/auth/vendor-customers/', payload);
        return data;
    },

    getCustomerCreditNotes: async (): Promise<CustomerCreditNote[]> => {
        if (!tokenStore.getAccess()) return [];
        const { data } = await apiClient.get(`${BASE}/customers/credit-notes/`);
        return unpackList<CustomerCreditNote>(data);
    },

    getVendorBills: async (): Promise<VendorBill[]> => {
        if (!tokenStore.getAccess()) return [];
        const { data } = await apiClient.get(`${BASE}/vendor-bills/`);
        return unpackList<VendorBill>(data);
    },

    postVendorBill: async (billId: number): Promise<VendorBill> => {
        const { data } = await apiClient.post(`${BASE}/vendor-bills/${billId}/post-bill/`);
        return data;
    },

    payVendorBill: async (
        billId: number,
        paymentMethod: 'cash' | 'bank' | 'card' = 'bank',
        amount?: number
    ): Promise<VendorBill> => {
        const { data } = await apiClient.post(`${BASE}/vendor-bills/${billId}/pay-bill/`, {
            payment_method: paymentMethod,
            ...(amount && amount > 0 ? { amount } : {}),
        });
        return data;
    },

    getVendorSuppliers: async (): Promise<VendorContact[]> => {
        if (!tokenStore.getAccess()) return [];
        const { data } = await apiClient.get(`${BASE}/vendors/list/`);
        return unpackList<VendorContact>(data);
    },

    createVendorSupplier: async (payload: {
        name: string;
        phone?: string;
        email?: string;
        address?: string;
        city?: string;
        tax_number?: string;
        commercial_register?: string;
        internal_notes?: string;
    }) => {
        const { data } = await apiClient.post('/auth/vendor-suppliers/', payload);
        return data;
    },

    getVendorRefunds: async (): Promise<any[]> => {
        if (!tokenStore.getAccess()) return [];
        const { data } = await apiClient.get(`${BASE}/vendors/refunds/`);
        return unpackList<any>(data);
    },

    getJournalEntries: async (): Promise<JournalEntry[]> => {
        if (!tokenStore.getAccess()) return [];
        const { data } = await apiClient.get(`${BASE}/journal-entries/`);
        return unpackList<JournalEntry>(data);
    },

    registerCustomerPayment: async (
        orderId: number,
        paymentMethod: 'cash' | 'bank' | 'card' = 'cash',
        amount?: number
    ) => {
        const mapped = paymentMethod === 'cash' ? 'cod' : paymentMethod;
        const { data } = await apiClient.post(`/sales/orders/${orderId}/pay-invoice/`, {
            payment_method: mapped,
            ...(amount && amount > 0 ? { amount } : {}),
        });
        return data;
    },

    getPartnerStatement: async (
        partnerType: 'customer' | 'supplier',
        partnerId: number,
        params?: { start_date?: string; end_date?: string }
    ): Promise<PartnerStatementPayload> => {
        const search = new URLSearchParams({
            partner_type: partnerType,
            partner_id: String(partnerId),
        });
        if (params?.start_date) search.set('start_date', params.start_date);
        if (params?.end_date) search.set('end_date', params.end_date);
        const { data } = await apiClient.get(`${BASE}/partners/statement/?${search.toString()}`);
        return data;
    },

    getGeneralLedger: async (): Promise<GeneralLedgerRow[]> => {
        if (!tokenStore.getAccess()) return [];
        const { data } = await apiClient.get(`${BASE}/general-ledger/`);
        return unpackList<GeneralLedgerRow>(data?.rows ? data.rows : data);
    },

    getProfitLoss: async (): Promise<ProfitLossReport> => {
        const { data } = await apiClient.get(`${BASE}/reports/profit-loss/`);
        return data;
    },

    getBalanceSheet: async (): Promise<BalanceSheetReport> => {
        const { data } = await apiClient.get(`${BASE}/reports/balance-sheet/`);
        return data;
    },

    getTrialBalance: async (): Promise<TrialBalanceReport> => {
        const { data } = await apiClient.get(`${BASE}/reports/trial-balance/`);
        return data;
    },

    getChartOfAccounts: async (): Promise<ChartAccount[]> => {
        if (!tokenStore.getAccess()) return [];
        const { data } = await apiClient.get(`${BASE}/chart-of-accounts/`);
        return unpackList<ChartAccount>(data);
    },

    createChartOfAccount: async (payload: {
        account_type: number;
        code: string;
        name: string;
        name_ar?: string;
        description?: string;
        allow_reconcile?: boolean;
        is_active?: boolean;
    }): Promise<ChartAccount> => {
        const { data } = await apiClient.post(`${BASE}/chart-of-accounts/`, payload);
        return data;
    },

    getJournals: async (): Promise<AccountingJournal[]> => {
        if (!tokenStore.getAccess()) return [];
        const { data } = await apiClient.get(`${BASE}/journals/`);
        return unpackList<AccountingJournal>(data);
    },

    createJournal: async (payload: {
        code: string;
        name: string;
        name_ar?: string;
        journal_type: 'sales' | 'purchase' | 'cash' | 'bank' | 'inventory' | 'misc';
        sequence?: number;
        is_active?: boolean;
    }): Promise<AccountingJournal> => {
        const { data } = await apiClient.post(`${BASE}/journals/`, payload);
        return data;
    },

    getAnalyticAccounts: async (): Promise<AnalyticAccount[]> => {
        if (!tokenStore.getAccess()) return [];
        const { data } = await apiClient.get(`${BASE}/analytic-accounts/`);
        return unpackList<AnalyticAccount>(data);
    },

    createAnalyticAccount: async (payload: {
        code: string;
        name: string;
        name_ar?: string;
        description?: string;
        is_active?: boolean;
    }): Promise<AnalyticAccount> => {
        const { data } = await apiClient.post(`${BASE}/analytic-accounts/`, payload);
        return data;
    },

    createManualJournalEntry: async (payload: {
        journal_id: number;
        entry_date?: string;
        reference?: string;
        description?: string;
        lines: Array<{
            account_id: number;
            analytic_account_id?: number | null;
            description?: string;
            debit?: string;
            credit?: string;
        }>;
    }): Promise<JournalEntry> => {
        const { data } = await apiClient.post(`${BASE}/journal-entries/`, payload);
        return data;
    },
};
